/**
 * @fileoverview The photo tray page: many photos in, plants out.
 *
 * Photos arrive in piles by the time they were taken. The piles are the
 * server's (audit batch 6a, A33 and A34; owner R13, 2026-10-18): GET /tray
 * answers every photo and the piles it makes of them, and this page draws
 * them (drawPiles). Every photo is shown to the model once (2026-10-06; it
 * used to be each pile's first only), a batch of the server's limits.TRAY_GUESS_BATCH at a time,
 * newest groups first, and the tray is read again after each batch, so each
 * pile comes back named, with a pick to agree with or change. What the photos
 * show corrects the clock on the server: seed packets and a photo Grow is
 * sure is another plant leave their pile, the pile is named by its surest
 * photo, and a pile from another day that looks like the same plant is
 * offered as one ("Same butternut as your Jun 12 photos? · Yes, one plant").
 * That puts the later pile's photos with the earlier, as a move does, with
 * Undo; filing it keeps each photo's own stage on its own day (POST
 * /api/tray/sort). Agreeing makes the plant, with the pile's photos on it,
 * and the pile goes. When the tray is empty the garden is populated, which
 * is the whole point of the page.
 *
 * Everything is said once: one line of intro, the bed the batch is from on
 * the count line, the subscription note above the piles. A pile is a strip
 * of pictures and one question (see PileCard). Touching a picture opens the
 * one sheet where a photograph is moved, given its own group, turned and
 * cropped ("Edit image", ImageEditDialog), or removed.
 *
 * A move is kept on the server (POST /tray/move), so a reload or a second
 * phone shows the piles as they were left, and its toast's Undo puts each
 * photo back where it was. Removing says "Remove", not "Not a plant"
 * (2026-10-02): a gardener removes a blurry photo of a plant too. Each photo
 * has its own ×, and each group a ⋯ to remove all of it, which asks first
 * since that is many at once. A removed photo is gone at once (POST /tray/remove) with a
 * short "1 photo removed" line that goes by itself and no Undo: once removed,
 * it is removed (owner, 2026-10-21, B45, as B26 for plants). The server keeps it
 * for its own photos.REMOVED_KEEP_HOURS and deletes it; the original stays on
 * their phone or in Google Photos.
 *
 * A plant's first photo filed from here is asked about sharing here too
 * ("Share your cherry tomato?", ShareMoments), once, as on the Plants page;
 * it used to wait for the Plants page to be opened.
 *
 * A photo of seed packets is found by the same guess (it says so too), stands
 * as a group of its own, and is read by the server in the same request as the guess (one more paid reading, with no
 * second request from the app; a reading deferred for the allowance is asked again once a visit). Its card lists the packets
 * to add, a plant for each (PacketPile); when the reader finds none, or
 * fails, or the gardener says "Ask about it as a plant" (its ⋯ menu), it is asked about
 * as a plant like any other.
 *
 * A photo of several kinds of plant (a tray of seedlings, a bouquet, a
 * harvest, a mixed bed) is found by the same guess, with no reading more,
 * and stands as a group of its own too. Its card lists the plants, a plant
 * for each (KindsPile); "Just one plant" asks about it as a plant like any
 * other. A plant Grow does not have is offered as the gardener's own, made
 * when it is filed; the catalogue is read again afterwards so the next card
 * knows it.
 *
 * The photo help's allowance (D23) is said above the piles: when a guess or
 * a packet reading is refused for it, which limit and when some comes back
 * (AiLimitNotice; a trial's offers Subscribe), and the cards fall back to
 * asking plainly; otherwise, once, quietly, when most of a subscriber's
 * month is used (AiNearNote).
 *
 * One column at most max-w-3xl wide, so on a desktop the bed's choice and
 * the piles don't stretch across the screen.
 *
 * The back link names the page the gardener came from (Plants, Today,
 * Seasons: the link there passes `state.from`, see TrayFrom). Arriving by
 * the Add photos tab, or straight from welcome, there is nothing to go
 * back to, so there is no link: "Back to plants" read wrongly to someone
 * who had never been on Plants.
 *
 * Where: the route /plants/photos (the Add photos tab; /photos goes there
 * too), inside the stages and plants providers (the beds' names are read by useBedNames: GET /locations?short=1).
 * API: GET /tray (useTray, the photos, the piles and `filed`); POST /tray/guess
 * { trayIds } (understandTray, ?tz=); POST /tray/not-packets; POST /tray/sort
 * (sortIntoNewPlant and sortIntoPlant); POST /tray/kinds (KindsPile); POST
 * /packets/confirm (PacketPile); PUT /tray/:id/image and PUT
 * /tray/:id/image/reset (Edit image); POST /tray/move and /tray/remove
 * (moveInTray, removeFromTray); the photo help's
 * allowance read again after each AI use.
 * Settings: TRAY_MOVE_UNDO_TOAST_MS
 * (config/tunables/garden); limits.TRAY_GUESS_BATCH (lib/growSettings); access.ai and
 * access.aiRevoked (useAiAccess); the router state's `from` (the back link).
 * The piles' own settings (photos.PILE_GAP_MINUTES, SPLIT_CONFIDENCE,
 * REMOVED_KEEP_HOURS) are the server's.
 * Words (en/es): tray.* (title, intro, photosFrom, noLocations, photosIn*,
 * loading, empty, guessLocked, guessLockedAction, sorted*, savedTo*,
 * sortFailed, aPlant, backTo*, pile.*), billing.aiRevoked,
 * plants.card.editImage, sitewide.loadFailed.
 * Failures on our side (a 500, no connection; D31, lib/serverFailure): a
 * tray that could not be read, and a group that could not be filed, say the
 * standard sentence (reported to Sentry, the support address a link) in
 * place of sitewide.loadFailed and tray.sortFailed (errorMessage); a move,
 * removal or Undo that fails is reported and says the standard sentence in a
 * toast (tray.pile.changeFailed for a refusal); a guess, packet reading or
 * "not packets" that fails on our side is reported too, the page carrying on
 * as it did.
 * Above the piles, "Filed by Grow" (FiledStrip, B160) lists the photos Grow was sure of and filed onto their plants by itself in
 * the last photos.FILED_SHOW_HOURS (the server's `filed`), each with Move (the one move sheet, MovePhotoSheet); with nothing
 * waiting the page says the strip and "Nothing to sort" below it. Words (en/es): tray.filed.*.
 * "Nothing to sort" (tray.empty) is said only when the tray has no piles and
 * nothing is on its way in: no batch being sent, and no landed photo still
 * waiting for its pile (B25). After packets or kinds are filed it first says where the plants went, with a
 * link to Plants (tray.filedOne, tray.filedMany, tray.filedLinkOne, tray.filedLink; L1, 2026-10-21).
 * Watch out: nothing is guessed for a gardener without the photo AI (Free, a
 * paused plan, AI switched off): every pile would have come back 402 and put
 * the page behind the wall.
 *
 * The one understanding effect (useTrayGuesses) and the move and removal
 * handlers (useTrayEdits) live in lib/ (audit batch 18, A104). The piles,
 * their cards, the photo's sheet, the confirm and the filing are TrayPiles
 * (D78, 2026-10-22), shared with the add pop-ups' "Check and save" sheet; this
 * page is unchanged around them: the intro, "Photos from" and Choose photos,
 * the count line, the loading and failure states, and "Nothing to sort".
 *
 * @module PhotoTray
 */

import { ShareMoments } from '@/features/share/ShareMoments';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { GrowLoader } from '@/shared/components/ui/GrowLoader';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { usePlantsContext } from '@/shared/hooks/usePlantsContext';
import { useTips } from '@/features/tips/useTips';
import { useBedNames } from '@/shared/hooks/useBedNames';
import { isServerFailure, rateLimitedText } from '@/shared/lib/serverFailure';
import { FailureText, ServerFailureNote } from '@/shared/components/ServerFailure';
import { drawPiles, useTray } from './lib/useTray';
import { NativeSelect } from '@/shared/components/ui/native-select';
import { TrayUploader } from './components/TrayUploader';
import { TrayPiles } from './components/TrayPiles';
import { FiledStrip } from './components/FiledStrip';


/** The pages that link to Add photos and say so in the link's state (`{ from }`). */
type TrayFrom = 'plants' | 'today' | 'season';

/** Where each of those pages is, and the words that lead back to it. */
const TRAY_BACK: Record<TrayFrom, { to: string; label: string }> = {
  plants: { to: '/plants', label: 'tray.backToPlants' },
  today: { to: '/today', label: 'tray.backToToday' },
  season: { to: '/season', label: 'tray.backToSeason' },
};

/**
 * The back link for the page the gardener came from, or null when they came
 * by the Add photos tab (or anything else that names no page).
 *
 * @param state - The router location's state.
 * @returns The link's target and translation key, or null.
 */
function trayBack(state: unknown): { to: string; label: string } | null {
  const from = (state as { from?: unknown } | null)?.from;
  return typeof from === 'string' && from in TRAY_BACK ? TRAY_BACK[from as TrayFrom] : null;
}

/** Add photos (/plants/photos): choose photos, then say what each group is. */
export const PhotoTray: React.FC = () => {
  const { reactTranslate: t } = useTranslation();
  // A tray that could not be read says so; it used to say "nothing to sort".
  const { data: trayData, isLoading, isError: trayFailed, error: trayError } = useTray();
  /** Every photo on the tray, removed ones included (none before it arrives). */
  const photos = React.useMemo(() => trayData?.photos ?? [], [trayData]);
  const { plants } = usePlantsContext();
  const { data: locations } = useBedNames();
  /** The link back to the page the gardener came from, or null. */
  const back = trayBack(useLocation().state);
  // Having opened Photos finishes any tip that was pointing here.
  const { happened } = useTips();
  React.useEffect(() => { happened('photos_opened'); }, [happened]);

  /** The live beds, for the "Photos from" choice. */
  const liveLocations = React.useMemo(() => locations ?? [], [locations]);
  /** True while a batch of chosen photos is being shrunk and sent (TrayUploader). */
  const [uploading, setUploading] = React.useState(false);
  // The bed the batch is from, asked once. The first bed that arrives is the default.
  const [batchLocation, setBatchLocation] = React.useState<number | null>(null);
  React.useEffect(() => {
    if (batchLocation === null && liveLocations[0]) setBatchLocation(liveLocations[0].id);
  }, [liveLocations, batchLocation]);
  /** The photos still to sort: the ones not removed (a removed photo is gone from the page, B45). */
  const shownPhotos = React.useMemo(() => photos.filter((photo) => !photo.removed), [photos]);
  /** The piles as drawn, the server's, with every removed photo left out (a pile with none left is not drawn). */
  const piles = React.useMemo(() => drawPiles(trayData), [trayData]);
  /**
   * Photos are still on their way into piles: a batch is being sent, or a photo
   * has landed (trayPhotoLanded puts it in the photos at once) and the tray has
   * not yet been read again with the pile the server put it in. While this
   * holds the tray is not empty, so "Nothing to sort" is not said (B25,
   * 2026-10-20: it used to stay under a batch going in).
   */
  const arriving = React.useMemo(() => {
    if (uploading) return true;
    const piled = new Set((trayData?.piles ?? []).flatMap((pile) => pile.photoIds));
    return photos.some((photo) => !photo.removed && !piled.has(photo.tray_id));
  }, [uploading, trayData, photos]);

  return (
    <div className="mx-auto w-full max-w-3xl p-4">
      {back && (
        <Link
          to={back.to}
          className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          {t(back.label)}
        </Link>
      )}
      <h1 className="text-2xl font-semibold font-body">{t('tray.title')}</h1>
      <p className="mb-4 mt-1 max-w-prose text-sm text-muted-foreground">{t('tray.intro')}</p>

      {/* The bed the next photos are from, asked before they are chosen: it
          is the one thing that has to be said first, and it only applies to
          what is added after it. */}
      {/* One row: where the photos are from, and the button that adds them,
          the same height and on one line, with the label above as on the cards. */}
      <div className="mb-3">
        <label htmlFor="tray-from" className="mb-1 block text-xs text-muted-foreground">{t('tray.photosFrom')}</label>
        <div className="flex items-stretch gap-2">
          <NativeSelect
            id="tray-from"
            className="min-w-0 flex-1"
            // The box and its list at the small size (owner, 2026-10-15: the list's words were huge).
            selectClassName="text-sm"
            value={batchLocation ?? ''}
            onChange={(event) => setBatchLocation(event.target.value ? Number(event.target.value) : null)}
            disabled={uploading || liveLocations.length === 0}
          >
            {liveLocations.length === 0 && <option value="">{t('tray.noLocations')}</option>}
            {liveLocations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
          </NativeSelect>
          <TrayUploader locationId={batchLocation} onUploadingChange={setUploading} />
        </div>
      </div>
      {piles.length > 0 && (
        <p className="mb-4 text-sm text-muted-foreground">
          {t(shownPhotos.length === 1 ? 'tray.photosInOne' : piles.length === 1 ? 'tray.photosInOneGroup' : 'tray.photosIn', { photos: shownPhotos.length, groups: piles.length })}
        </p>
      )}

      {/* The photos Grow was sure of and filed by itself lately, above the piles (B160); "Nothing to sort" still shows under it. */}
      <FiledStrip filed={trayData?.filed} />

      {isLoading ? (
        <GrowLoader variant="block" label={t('tray.loading')} className="py-8" />
      ) : !trayData && trayFailed ? (
        // On our side: the standard sentence, with no "Try again"; the request limit: its sentence; otherwise sitewide.loadFailed.
        isServerFailure(trayError)
          ? <ServerFailureNote error={trayError} where="Tray" className="py-8 text-center" />
          : <p role="alert" className="py-8 text-center text-sm text-destructive"><FailureText text={rateLimitedText(trayError) ?? t('sitewide.loadFailed')} /></p>
      ) : (
        <TrayPiles
          uploading={uploading}
          // Truly empty only when nothing is on its way in; while a batch goes
          // in, the button's count says what is happening and the piles follow.
          empty={(filedTo) => (arriving ? null : (
            <div className="py-8 text-center text-sm text-muted-foreground">
              {filedTo && (
                <p className="mb-2 text-foreground">
                  {t(filedTo.count === 1 ? 'tray.filedOne' : 'tray.filedMany', { count: filedTo.count, bed: filedTo.bed })}{' '}
                  <Link to="/plants" className="font-medium underline underline-offset-2">{t(filedTo.count === 1 ? 'tray.filedLinkOne' : 'tray.filedLink')}</Link>
                </p>
              )}
              <p>{t('tray.empty')}</p>
            </div>
          ))}
        />
      )}

      {/* "Share your {plant}?" when a first photo is filed here, as on the Plants page. */}
      <ShareMoments plants={plants ?? []} />
    </div>
  );
};

export default PhotoTray;
